/**
 * What happened last time you were here.
 *
 * ## The one thing this panel must never do
 *
 * Show a figure without showing what it is a figure ABOUT. "You are 8-6 here"
 * is a different claim depending on whether "here" means *ES, long, in a range,
 * in the first hour* or just *ES*. The server relaxes the query until the
 * sample is worth reporting and says what it dropped; this panel renders the
 * matched facets as a sentence and the dropped ones as an explicit widening
 * note. A design that hid either would be misleading by omission, not merely
 * incomplete.
 *
 * ## Small samples are stated, not hidden
 *
 * Below the floor the panel still renders — with the count and the sentence
 * saying it is too few. An empty panel would let the trader assume the mirror
 * had nothing to say about them, when what it has to say is "not enough yet".
 *
 * Counting, no model. CLAUDE.md non-negotiable 4: descriptive throughout.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, fmtNum, fmtPct, fmtUsd } from '../lib/api.js';
import type { PrecedentResponse } from '../lib/api.js';

export function PrecedentPanel({ symbol, direction }: {
  symbol: string;
  direction?: 'long' | 'short';
}): JSX.Element | null {
  const [p, setP] = useState<PrecedentResponse | null>(null);

  useEffect(() => {
    let live = true;
    setP(null);
    void api.precedent(symbol, direction)
      .then((r) => { if (live) setP(r); })
      .catch(() => { /* One panel failing is never worth blanking a page. */ });
    return () => { live = false; };
  }, [symbol, direction]);

  if (!p) return null;

  // No history at all is worth one line, not a table of zeroes.
  if (p.reason === 'no_history') {
    return (
      <div className="prec prec-quiet">
        <b>WHEN YOU HAVE BEEN HERE</b>
        <p className="prec-headline">{p.headline}</p>
      </div>
    );
  }

  const s = p.summary;
  const thin = p.reason === 'too_few_trades';

  return (
    <div className={`prec${thin ? ' prec-thin' : ''}`}>
      <b>WHEN YOU HAVE BEEN HERE</b>

      {/* The situation, spelled out. Without this the numbers below are
          unreadable — a win rate with no stated scope is a rumour. */}
      <p className="prec-where">
        {p.matched.map((f) => f.phrase).join(', ')}
      </p>

      <p className="prec-headline">{p.headline}</p>

      {!thin && (
        <>
          <div className="prec-figures">
            <span className="prec-record mono">
              {s.winCount}–{s.lossCount}
              {s.scratchCount > 0 && <i>·{s.scratchCount} BE</i>}
            </span>
            <span className="prec-fig">
              <i>WIN RATE</i>
              <span className="mono">{fmtPct(s.winRate, 0)}</span>
            </span>
            <span className="prec-fig">
              <i>EXPECTANCY</i>
              <span className="mono">{fmtUsd(s.expectancy, { sign: true })}</span>
            </span>
            {s.avgRMultiple !== null && (
              <span className="prec-fig">
                <i>AVG R</i>
                <span className="mono">{fmtNum(s.avgRMultiple, 2)}R</span>
              </span>
            )}
            <span className="prec-fig">
              <i>NET</i>
              <span className="mono">{fmtUsd(s.netPnl, { sign: true })}</span>
            </span>
          </div>

          {/* Most recent first, so a run that just ended is visible rather than
              averaged away. Five is a glance; the journal is where a trader
              reads the rest. */}
          {p.trades.length > 0 && (
            <ul className="prec-trades">
              {p.trades.slice(0, 5).map((t) => (
                <li key={t.id}>
                  <span className="prec-t-date mono">
                    {new Date(t.openedAt).toISOString().slice(5, 10)}
                  </span>
                  <span className={`prec-t-dir dir-${t.direction}`}>
                    {t.direction === 'long' ? 'L' : 'S'}
                  </span>
                  <span className="prec-t-sym">{t.symbol}</span>
                  <span className={`prec-t-pnl mono ${Number(t.netPnl) >= 0 ? 'up' : 'down'}`}>
                    {fmtUsd(t.netPnl, { sign: true })}
                  </span>
                  {t.rMultiple !== null && (
                    <span className="prec-t-r mono">{fmtNum(t.rMultiple, 1)}R</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/*
        What the query gave up to get here. Rendered as its own line rather than
        folded into the headline, because it is the caveat that decides how much
        the figures above are worth.
      */}
      {p.relaxed.length > 0 && (
        <p className="prec-relaxed">
          Not narrow enough on {p.relaxed.map((f) => f.label.toLowerCase()).join(', ')} —
          {' '}widened to reach {p.sampleSize} trades.
        </p>
      )}

      <span className="prec-source">
        Counted from your own journal. No model involved.
      </span>
    </div>
  );
}
